import { useState } from 'react';

import Button from '@/componentsv2/Button';
import InfoTip from '@/componentsv2/InfoTip';

import { formatDate } from '../components/EngagementHub/format';
import CloseEngagementDialog from './CloseEngagementDialog';
import Section from './Section';
import { formatEngagementMoney, formatHours } from './money';
import { TERMS, type TermCopy } from './termCopy';
import type { BillingView, EngagementView } from './viewModel';

interface SettingsPanelProps {
  view: EngagementView;
}

const CADENCE_LABEL: Record<string, string> = {
  biweekly: 'Every two weeks',
  monthly: 'Monthly',
};

/** Pacing periods, as the noun that follows "per". */
const PACING_PERIOD: Record<string, string> = {
  weekly: 'week',
  monthly: 'month',
};

/**
 * The engagement's own terms, then the account configuration behind them.
 *
 * The split matters for §6.3: dates, cadence and pacing belong to the
 * engagement and are **not** viewer-gated, so the expert sees them too. The
 * account settings block is customer-side only, and the api says so by
 * resolving `primaryProject` — and with it the billing account — to null.
 *
 * Every term carries its definition (ENG-5757), from `termCopy.ts` so the
 * hire drawer and the offer terms say the same thing. The review timers are
 * listed in the order they fire for hours nobody answers — reminder, second
 * notice, automatic approval — with the separate dispute clock after them.
 */
export default function SettingsPanel({ view }: SettingsPanelProps): JSX.Element {
  const billing = view.billing;
  const [closing, setClosing] = useState(false);

  return (
    <div className="flex flex-col gap-24">
      <Section title="Engagement terms">
        <dl className="grid grid-cols-1 gap-16 sm:grid-cols-2">
          <Row label="Start date">{view.startDate === null ? '—' : formatDate(view.startDate)}</Row>
          <Row label="End date">{view.endDate === null ? '—' : formatDate(view.endDate)}</Row>
          <Row term={TERMS.billingCadence}>
            {view.billingCadence === null ? '—' : (CADENCE_LABEL[view.billingCadence] ?? '—')}
          </Row>
          <Row term={TERMS.ntePacing}>{pacingLabel(view)}</Row>
        </dl>
      </Section>

      <Section title="Account settings">
        {billing === null ? (
          <p className="text-on-surface-muted body-2">
            Account settings are visible to the customer side only.
          </p>
        ) : (
          <dl className="grid grid-cols-1 gap-16 sm:grid-cols-2">
            <Row term={TERMS.minimumHours}>
              {billing.minHours === null ? '—' : formatHours(billing.minHours)}
            </Row>
            <Row term={TERMS.governanceThreshold}>{thresholdLabel(billing)}</Row>
            <Row term={TERMS.reviewReminder}>{timer(billing.reminderDays)}</Row>
            <Row term={TERMS.mediationWindow}>{timer(billing.mediationDays)}</Row>
            <Row term={TERMS.hoursAutoApprove}>{timer(billing.hoursAutoApproveDays)}</Row>
            <Row term={TERMS.disputeRecommendation}>{timer(billing.disputeArbitrationDays)}</Row>
            <Row term={TERMS.nteAlertThreshold}>
              {billing.nteAlertPct === null ? '—' : `${billing.nteAlertPct}%`}
            </Row>
            <Row term={TERMS.invoiceTerms}>{days(billing.invoiceTermsDays)}</Row>
          </dl>
        )}
      </Section>

      {/*
       * Close lives here rather than in the Hub's action strip. The strip holds
       * the actions a state is waiting on — make an offer, accept, approve — and
       * close is none of those: it is available for the whole life of an active
       * engagement and is destructive, so a permanent primary-position button
       * would put an irreversible action where the next expected step goes.
       *
       * `viewerCanClose` is the api's answer, not a role test here: asking the
       * client's own role would be a second copy of engagement_policies.go, and
       * it is ACTIVE-only, so the control cannot outlive what it acts on
       * (ENG-5688).
       */}
      {view.viewerCanClose ? (
        <Section title="Close engagement">
          <p className="text-on-surface-muted body-2">
            Ends the engagement immediately. The expert is notified and cannot submit further hours,
            and a closed engagement cannot be reopened.
          </p>
          <div className="mt-16">
            <Button variant="destructive" onClick={() => setClosing(true)}>
              Close engagement
            </Button>
          </div>
        </Section>
      ) : null}

      {/*
        Mounted only while open: the dialog owns a mutation hook, so keeping it
        in the tree would put an Apollo dependency on every Settings render, and
        a fresh mount is also what resets a refusal message between attempts.
      */}
      {closing ? (
        <CloseEngagementDialog engagementId={view.id} open onCancel={() => setClosing(false)} />
      ) : null}
    </div>
  );
}

/**
 * `none` is a real answer, not a missing one — an unpaced engagement lets the
 * expert draw the whole NTE whenever, which is worth stating rather than
 * rendering as a dash.
 */
function pacingLabel(view: EngagementView): string {
  if (view.pacingType === null) return '—';
  const period = PACING_PERIOD[view.pacingType];
  if (period === undefined) return 'Not paced';
  if (view.pacingValue === null) return `Paced per ${period}`;
  return `${formatHours(view.pacingValue)} per ${period}`;
}

/**
 * Both units, always (ENG-5757). The account stores the threshold in credits,
 * but the gate compares dollars, and an engagement over it is invoiced in USD,
 * so a bare credit count had nothing on the page to be compared with. The
 * dollar figure is the api's, never a multiplication here. It drops out on its
 * own when the api could not price it, leaving the credit count.
 *
 * No SMB branch: an SMB account cannot enable engagement billing settings
 * (the api refuses it, RB-12), so there is never a threshold to show one.
 */
function thresholdLabel(billing: BillingView): string {
  const credits = billing.governanceThresholdCredits;
  if (credits === null) return '—';
  const count = credits === 1 ? '1 credit' : `${credits} credits`;
  const usd = billing.governanceThresholdUsd;
  return usd === null ? count : `${count} (${formatEngagementMoney(usd.cents, usd.currency)})`;
}

function days(value: number | null): string {
  if (value === null) return '—';
  return value === 1 ? '1 day' : `${value} days`;
}

/**
 * A review timer. Null is not missing data: the api documents a null timer
 * column as "disables the step", so it reads Off. A dash left the customer
 * unable to tell a step that never fires from one we failed to load.
 */
function timer(value: number | null): string {
  return value === null ? 'Off' : days(value);
}

/**
 * A term row. The dates are the only rows without a definition: they are
 * plain calendar dates, and ENG-5757 scoped the definitions to the ten
 * governance and billing terms.
 */
function Row({
  label,
  term,
  children,
}: {
  label?: string;
  term?: TermCopy;
  children: React.ReactNode;
}) {
  return (
    <div>
      <dt className="flex items-center gap-4 text-on-surface-muted subtitle-2">
        {term?.label ?? label}
        {term ? <InfoTip term={term.label} definition={term.definition} /> : null}
      </dt>
      <dd className="mt-4 text-on-surface body-2">{children}</dd>
    </div>
  );
}
